import { useEffect, useState } from 'react';

import { useDispatch, useSelector } from 'react-redux';

import DeleteIcon from 'assets/settings/components/DeleteIcon';
import EditIcon from 'assets/settings/components/EditIcon';
import Select from 'common-ui/select/Select';
import Pagination from 'common-ui/table/Pagination';
import { getAdministrativeRegions, getCities } from 'features/newsales/newsalesActions';
import {
  selectCities,
  selectCitiesCurrentPage,
  selectCitiesDeleteSuccess,
  selectCitiesLoading,
  selectCitiesPagesCount,
  selectCitiesSearchData,
  selectCitiesSelectedCountryId,
  selectCitiesSelectedDistrictId,
  selectCitiesSuccess,
  selectCitiesUpdateSuccess,
  setCitiesCreateSuccess,
  setCitiesCurrentPage,
  setCitiesDeleteSuccess,
  setCitiesSearchData,
  setCitiesSelectedCountry,
  setCitiesSelectedDistrict,
  setCitiesUpdateSuccess,
} from 'features/newsales/newsalesSlice';

import {
  ActionsCell,
  EmptyState,
  FilterFlex,
  FilterSection,
  IconButton,
  LoadingSpinner,
  PaginationWrapper,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableHeaderActions,
  TableRow,
} from '../Addresses.styles';

const CityVillagePage = ({ onEdit, onDelete, allCountries, onRegionsChange }) => {
  const dispatch = useDispatch();
  const [regionsForCities, setRegionsForCities] = useState([]);

  const cities = useSelector(selectCities);
  const citiesLoading = useSelector(selectCitiesLoading);
  const citiesCurrentPage = useSelector(selectCitiesCurrentPage);
  const citiesPagesCount = useSelector(selectCitiesPagesCount);
  const citiesSearchData = useSelector(selectCitiesSearchData);
  const citiesCreateSuccess = useSelector(selectCitiesSuccess);
  const citiesUpdateSuccess = useSelector(selectCitiesUpdateSuccess);
  const citiesDeleteSuccess = useSelector(selectCitiesDeleteSuccess);
  const citySelectedCountryId = useSelector(selectCitiesSelectedCountryId);
  const citySelectedDistrictId = useSelector(selectCitiesSelectedDistrictId);

  useEffect(() => {
    if (citySelectedCountryId) {
      dispatch(
        getAdministrativeRegions({
          countryId: citySelectedCountryId,
          limit: 1000,
        })
      ).then((result) => {
        if (result.payload && result.payload.data) {
          setRegionsForCities(result.payload.data);
          onRegionsChange(result.payload.data);
        }
      });
    }
  }, [dispatch, citySelectedCountryId, onRegionsChange]);

  useEffect(() => {
    if (citySelectedCountryId && citySelectedDistrictId) {
      dispatch(
        getCities({
          countryId: citySelectedCountryId,
          districtId: citySelectedDistrictId,
          page: citiesCurrentPage,
          limit: citiesSearchData.limit,
          search: citiesSearchData.search,
        })
      );
    }
  }, [
    dispatch,
    citySelectedCountryId,
    citySelectedDistrictId,
    citiesCurrentPage,
    citiesSearchData.limit,
    citiesSearchData.search,
  ]);

  useEffect(() => {
    if (citiesCreateSuccess) {
      dispatch(setCitiesCreateSuccess(false));
      if (citySelectedCountryId && citySelectedDistrictId) {
        dispatch(
          getCities({
            countryId: citySelectedCountryId,
            districtId: citySelectedDistrictId,
            page: citiesCurrentPage,
            limit: citiesSearchData.limit,
            search: citiesSearchData.search,
          })
        );
      }
    }
  }, [
    citiesCreateSuccess,
    dispatch,
    citySelectedCountryId,
    citySelectedDistrictId,
    citiesCurrentPage,
    citiesSearchData.limit,
    citiesSearchData.search,
  ]);

  useEffect(() => {
    if (citiesUpdateSuccess) {
      dispatch(setCitiesUpdateSuccess(false));
    }
  }, [citiesUpdateSuccess, dispatch]);

  useEffect(() => {
    if (citiesDeleteSuccess) {
      dispatch(setCitiesDeleteSuccess(false));
      if (cities?.length === 1 && citiesCurrentPage > 1) {
        dispatch(setCitiesCurrentPage(citiesCurrentPage - 1));
      } else {
        // Refetch current page data after deletion
        if (citySelectedCountryId && citySelectedDistrictId) {
          dispatch(
            getCities({
              countryId: citySelectedCountryId,
              districtId: citySelectedDistrictId,
              page: citiesCurrentPage,
              limit: citiesSearchData.limit,
              search: citiesSearchData.search,
            })
          );
        }
      }
    }
  }, [
    citiesDeleteSuccess,
    dispatch,
    cities,
    citiesCurrentPage,
    citySelectedCountryId,
    citySelectedDistrictId,
    citiesSearchData.limit,
    citiesSearchData.search,
  ]);

  const handleCityCountrySelect = (selectedOption) => {
    const countryId = selectedOption ? selectedOption.value : null;
    dispatch(setCitiesSelectedCountry(countryId));
  };

  const handleCityDistrictSelect = (selectedOption) => {
    const districtId = selectedOption ? selectedOption.value : null;
    dispatch(setCitiesSelectedDistrict(districtId));
  };

  const handlePageChange = (page) => {
    dispatch(setCitiesCurrentPage(page));
  };

  const handleRowCountChange = (count) => {
    dispatch(setCitiesSearchData({ limit: count, offset: 0 }));
    dispatch(setCitiesCurrentPage(1));
  };

  const cityCountryOptions = allCountries.map((country) => ({
    value: country.id,
    label: country.name,
  }));

  const cityRegionOptions = regionsForCities.map((region) => ({
    value: region.id,
    label: region.name,
  }));

  const citySelectedCountryOption = citySelectedCountryId
    ? cityCountryOptions.find((option) => option.value === citySelectedCountryId)
    : null;

  const citySelectedDistrictOption = citySelectedDistrictId
    ? cityRegionOptions.find((option) => option.value === citySelectedDistrictId)
    : null;

  return (
    <>
      <FilterFlex>
        <FilterSection $noBorder>
          <Select
            label="Select Country"
            value={citySelectedCountryOption}
            onChange={handleCityCountrySelect}
            options={cityCountryOptions}
            placeholder="Select a country"
            isClearable
            isDisabled={allCountries.length === 0}
          />
        </FilterSection>
        <FilterSection>
          <Select
            label="Select Region"
            value={citySelectedDistrictOption}
            onChange={handleCityDistrictSelect}
            options={cityRegionOptions}
            placeholder="Select a region"
            isClearable
            isDisabled={!citySelectedCountryId || regionsForCities.length === 0}
          />
        </FilterSection>
      </FilterFlex>

      {citiesLoading ? (
        <LoadingSpinner>Loading...</LoadingSpinner>
      ) : !citySelectedCountryId ? (
        <EmptyState>Please select a country to view cities.</EmptyState>
      ) : !citySelectedDistrictId ? (
        <EmptyState>Please select a region to view cities.</EmptyState>
      ) : !cities || cities.length === 0 ? (
        <EmptyState>No cities found.</EmptyState>
      ) : (
        <>
          <Table>
            <TableHead>
              <TableRow>
                <TableHeader>Name</TableHeader>
                <TableHeaderActions>Actions</TableHeaderActions>
              </TableRow>
            </TableHead>
            <TableBody>
              {cities.map((item) => (
                <TableRow key={item.id}>
                  <TableCell>{item.name}</TableCell>
                  <ActionsCell>
                    <IconButton onClick={() => onEdit(item)}>
                      <EditIcon size={18} color="#2D6CDF" />
                    </IconButton>
                    <IconButton onClick={() => onDelete(item)}>
                      <DeleteIcon size={18} color="#FB2C36" />
                    </IconButton>
                  </ActionsCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>

          {citiesPagesCount > 1 && (
            <PaginationWrapper>
              <Pagination
                currentPage={citiesCurrentPage}
                totalPages={citiesPagesCount}
                onPageChange={handlePageChange}
                handleRowCountChange={handleRowCountChange}
                dataCount={cities.length}
                count={citiesSearchData.limit}
                editableRowCount
              />
            </PaginationWrapper>
          )}
        </>
      )}
    </>
  );
};

export default CityVillagePage;
